import { FiLink } from 'react-icons/fi';

export const advancedCohesionData = {
  courseId: 'advanced-cohesion',
  courseType: 'writing-course',
  title: 'Advanced Cohesion Devices',
  icon: FiLink,
  overview: {
    impact: 'Achieve Band 7+ coherence and cohesion scores',
    focus: 'Reference words, lexical cohesion, thematic progression, and paragraph transitions',
    uses: 'IELTS Writing Tasks 1 and 2 — paragraph-level fluency for Band 7-9',
    duration: '25-30 minutes'
  },
  sections: [
    {
      title: 'Reference and Substitution',
      content: `<p>Reference words replace previously mentioned nouns or ideas, preventing repetition and creating <strong>textual flow</strong>. Substitution and ellipsis let you omit or replace repeated elements.</p>
<p><strong>Types of reference:</strong></p>
<ul>
  <li><strong>Pronoun reference:</strong> <em>The government introduced a new policy. <u>It</u> aimed to reduce inequality.</em></li>
  <li><strong>Demonstrative reference:</strong> <em>Air pollution causes respiratory disease. <u>This problem</u> is particularly acute in megacities.</em></li>
  <li><strong>"Such" reference:</strong> <em>Candidates may face technical failures during online exams. <u>Such disruptions</u> undermine the validity of results.</em></li>
  <li><strong>"The former / the latter":</strong> <em>Students can choose arts or sciences. <u>The latter</u> tends to offer higher starting salaries.</em></li>
</ul>
<p><strong>Substitution</strong> replaces a word with a general one: <em>"Many students passed. Only a few did not <u>do so</u>."</em></p>
<p><strong>Ellipsis</strong> omits repeated words: <em>"Some agree and some [do] not."</em></p>`,
      points: [
        'Use "this/these" + a summary noun (this trend, these factors) rather than bare "this" — it avoids ambiguity.',
        '"The former" and "the latter" work best when exactly two items are being compared.',
        'Substitution with "do so" or "one" sounds more academic than simply repeating the verb.',
        'Ellipsis is common in academic writing but must not create confusion about what is omitted.'
      ]
    },
    {
      title: 'Lexical Cohesion Techniques',
      content: `<p>Lexical cohesion connects sentences through <strong>vocabulary relationships</strong> rather than grammar. It is the most natural-sounding cohesion method and is essential for Band 8+.</p>
<p><strong>Key techniques:</strong></p>
<ul>
  <li><strong>Synonyms / near-synonyms:</strong> <em>increase → rise → growth → surge → escalation</em></li>
  <li><strong>Superordinates (general words):</strong> <em>"Doctors, nurses, and pharmacists… <u>These healthcare professionals</u>…"</em></li>
  <li><strong>Collocations:</strong> <em>"commit a crime" → "criminal behaviour" → "offending rates"</em> — using related word families ties ideas together.</li>
  <li><strong>Repetition of key terms:</strong> Strategic repetition of a central concept (e.g., "sustainability") anchors the reader.</li>
</ul>
<p><strong>Example:</strong> <em>"Urban sprawl has accelerated dramatically. <u>This unchecked expansion</u> consumes agricultural land and fragments wildlife habitats, creating <u>ecological consequences</u> that are difficult to reverse."</em></p>
<p>Notice how "urban sprawl" becomes "unchecked expansion" and the effects are labelled "ecological consequences" — no simple linker is needed.</p>`,
      points: [
        'Vary vocabulary but keep the meaning clear — forced synonyms confuse the reader.',
        'Use superordinates to summarise lists (e.g., "apples, rice, and bread" → "these foodstuffs").',
        'Collocational chains (related word families) are the hallmark of Band 8-9 writing.',
        'Do not over-rely on a thesaurus — choose synonyms you can use accurately.'
      ]
    },
    {
      title: 'Thematic Progression (Known-to-New)',
      content: `<p>Thematic progression means each sentence begins with <strong>known information</strong> (from the previous sentence) and ends with <strong>new information</strong>. This creates a natural logical flow.</p>
<p><strong>Three common patterns:</strong></p>
<ol>
  <li><strong>Linear progression:</strong> The new info in sentence 1 becomes the topic of sentence 2.<br/>
  <em>"The study examined 500 participants. <u>These participants</u> were divided into two groups. <u>Each group</u> received different interventions."</em></li>
  <li><strong>Constant theme:</strong> Multiple sentences share the same topic.<br/>
  <em>"<u>Renewable energy</u> reduces emissions. <u>It</u> also creates jobs. Furthermore, <u>it</u> reduces dependence on imports."</em></li>
  <li><strong>Derived themes:</strong> Sub-topics branch from one overarching theme.<br/>
  <em>"<u>Education reform</u> has three dimensions. <u>Curriculum changes</u> address content. <u>Teacher training</u> improves delivery. <u>Assessment reform</u> measures outcomes."</em></li>
</ol>
<p>Sentences that violate known-to-new order feel disjointed even when grammatically correct.</p>`,
      points: [
        'Start sentences with old/given information — readers process known-first more easily.',
        'Put the most important new detail at the end of a sentence (the "stress position").',
        'Mix progression patterns within an essay to avoid monotony.',
        'If a paragraph feels choppy, check whether you are violating known-to-new order.'
      ]
    },
    {
      title: 'Paragraph-Level Transitions',
      content: `<p>Band 7+ essays connect paragraphs through <strong>ideas</strong>, not just linking words. Over-reliance on "Firstly… Secondly… In conclusion…" is a Band 5-6 habit.</p>
<p><strong>Advanced transition strategies:</strong></p>
<ul>
  <li><strong>Echo transition:</strong> Repeat a keyword from the end of one paragraph at the start of the next.<br/>
  <em>"…this significantly reduces carbon emissions. ¶ <u>Emission reduction</u>, however, is only part of the solution."</em></li>
  <li><strong>Pivot transition:</strong> Acknowledge the previous point, then shift direction.<br/>
  <em>"While the economic benefits are clear, the <u>social implications</u> deserve equal attention."</em></li>
  <li><strong>Question transition:</strong> Pose a rhetorical question to bridge paragraphs.<br/>
  <em>"…education improves individual outcomes. ¶ But <u>does this translate into broader societal gains</u>?"</em></li>
  <li><strong>Summary-advance:</strong> Briefly summarise the previous paragraph, then introduce the next idea.<br/>
  <em>"Having established that urbanisation drives economic growth, it is necessary to examine <u>the environmental cost</u>."</em></li>
</ul>`,
      points: [
        'Use one transition strategy per paragraph break — mixing them across the essay sounds sophisticated.',
        'Remove "Firstly/Secondly/Thirdly" scaffolding in final drafts if you can replace it with idea-based transitions.',
        'The first sentence of every body paragraph should do double duty: transition AND state the new topic.',
        'Read your essay aloud — if you need to pause or re-read between paragraphs, the transition is weak.'
      ]
    }
  ],
  exercises: [
    {
      title: 'Identify Reference Words',
      type: 'select',
      instructions: 'Read each passage and identify what the highlighted reference word refers to.',
      questions: [
        {
          text: '"Governments have increased spending on infrastructure. THIS has led to improved living standards." — What does "THIS" refer to?',
          options: [
            'Living standards',
            'Increased spending on infrastructure',
            'Governments',
            'Infrastructure projects'
          ],
          answer: 'Increased spending on infrastructure',
          explanation: '"This" is a demonstrative reference pointing back to the entire action described — the increase in infrastructure spending. For clarity, writing "This investment" would be even better.'
        },
        {
          text: '"Students can specialise in medicine or engineering. THE LATTER offers more diverse career paths." — What does "the latter" refer to?',
          options: [
            'Medicine',
            'Engineering',
            'Students',
            'Career paths'
          ],
          answer: 'Engineering',
          explanation: '"The latter" refers to the second of two items mentioned. Medicine is "the former" and engineering is "the latter".'
        },
        {
          text: '"Deforestation, illegal mining, and overfishing threaten biodiversity. SUCH PRACTICES must be regulated." — What does "such practices" refer to?',
          options: [
            'Only deforestation',
            'All three activities: deforestation, illegal mining, and overfishing',
            'Biodiversity threats in general',
            'Government regulations'
          ],
          answer: 'All three activities: deforestation, illegal mining, and overfishing',
          explanation: '"Such practices" is a superordinate reference that summarises all three specific activities mentioned. This is a sophisticated cohesive device — labelling the list with a category noun.'
        }
      ]
    },
    {
      title: 'Replace with Cohesive Alternatives',
      type: 'fill-in',
      instructions: 'Replace the repeated word with a cohesive alternative. Type the best replacement word or phrase.',
      questions: [
        {
          text: '"Pollution is increasing in cities. Pollution affects the health of millions." Replace the second "Pollution" with ___.',
          answer: 'This issue',
          explanation: '"This issue" uses demonstrative reference + a summary noun to replace the repeated word. Alternatives like "This environmental problem" or "It" would also work.'
        },
        {
          text: '"Many students struggle with mathematics. Many students also find science difficult." Replace the second "Many students" with ___.',
          answer: 'They',
          explanation: 'A simple pronoun "They" eliminates the repetition while maintaining clarity, since the referent is obvious from the previous sentence.'
        },
        {
          text: '"The government built new hospitals. The government also invested in medical training." Replace the second "The government" with ___.',
          answer: 'It',
          explanation: 'Using "It" as a pronoun reference is the simplest fix. For more sophistication, you could write "The administration" (synonym) or restructure: "In addition to building new hospitals, it invested in medical training."'
        },
        {
          text: '"Teachers, doctors, and engineers all benefit from continuing education. Teachers, doctors, and engineers should be given time for professional development." Replace the second list with ___.',
          answer: 'These professionals',
          explanation: '"These professionals" is a superordinate that summarises the entire list. It is more concise and cohesive than repeating all three occupations.'
        }
      ]
    },
    {
      title: 'Choose the Best Cohesive Device',
      type: 'select',
      instructions: 'Select the option that best improves cohesion between the sentences.',
      questions: [
        {
          text: '"Remote work reduces commuting time. ___, it can increase feelings of isolation." Which transition works best?',
          options: [
            'Firstly',
            'However',
            'In addition',
            'For example'
          ],
          answer: 'However',
          explanation: 'The two sentences present contrasting ideas (benefit vs drawback), so a concessive transition like "However" is appropriate. "In addition" would wrongly suggest the second point supports the first.'
        },
        {
          text: '"The city invested in cycling infrastructure. ___, the number of cyclists tripled within two years." Which transition is best?',
          options: [
            'Nevertheless',
            'On the other hand',
            'As a result',
            'Similarly'
          ],
          answer: 'As a result',
          explanation: 'The second sentence is a consequence of the first (investment → more cyclists), so a cause-effect transition like "As a result" is correct.'
        },
        {
          text: 'Which paragraph transition is most sophisticated? Ending of previous paragraph: "…renewable energy is clearly the most sustainable option."',
          options: [
            'Secondly, I will discuss the economic costs.',
            'The economic costs of this transition, however, cannot be ignored.',
            'Another point is about the economy.',
            'Next, the economy.'
          ],
          answer: 'The economic costs of this transition, however, cannot be ignored.',
          explanation: 'This uses an echo transition ("this transition" refers back) combined with a concessive pivot ("however"). It bridges paragraphs through meaning rather than mechanical numbering.'
        },
        {
          text: '"The survey gathered data from 1,000 respondents. ___ were selected randomly from five urban centres." Which reference maintains cohesion?',
          options: [
            'The respondents in the survey',
            'They',
            'These participants',
            'People'
          ],
          answer: 'These participants',
          explanation: '"These participants" uses demonstrative reference + a near-synonym for "respondents." It avoids repetition while being specific. Bare "They" works but "These participants" is more precise and academic.'
        }
      ]
    },
    {
      title: 'Rewrite for Better Cohesion',
      type: 'writing',
      instructions: 'Rewrite the passage using advanced cohesion devices to eliminate repetition and improve flow.',
      questions: [
        {
          text: 'Rewrite this paragraph to improve cohesion: "Urbanisation is increasing rapidly. Urbanisation causes environmental problems. Environmental problems include air pollution and deforestation. Air pollution and deforestation affect human health."',
          example: 'Urbanisation is increasing at an unprecedented rate, bringing with it a range of environmental consequences. Chief among these are air pollution and deforestation, both of which pose significant risks to human health.',
          tip: 'Use reference words ("these," "such," "it"), synonyms, and sentence combining to eliminate the four repetitions while keeping all the original information.'
        },
        {
          text: 'Rewrite to improve the paragraph transition: "…education reform is essential for economic growth. Education is also important for reducing inequality."',
          example: '…education reform is essential for economic growth. Beyond its economic impact, however, such reform plays an equally vital role in reducing social inequality.',
          tip: 'Use an echo transition that picks up from the end of the first sentence and pivots to the new angle. Avoid starting the new sentence with the same subject.'
        }
      ]
    },
    {
      title: 'Identify Cohesion Errors',
      type: 'select',
      instructions: 'Find the cohesion problem in each passage.',
      questions: [
        {
          text: '"Several factors contribute to obesity. This is a growing concern worldwide." — What is the cohesion error?',
          options: [
            '"This" is ambiguous — it could refer to "several factors" or "obesity"',
            'The sentence is too short',
            'There is no error',
            '"Worldwide" is in the wrong position'
          ],
          answer: '"This" is ambiguous — it could refer to "several factors" or "obesity"',
          explanation: 'Bare "this" without a summary noun creates ambiguity. Fix: "This health crisis is a growing concern worldwide" or "Obesity is a growing concern worldwide" — making the reference explicit.'
        },
        {
          text: '"The experiment produced unexpected results. Moreover, the weather was sunny that day." — What is the cohesion problem?',
          options: [
            '"Moreover" is too formal',
            'The second sentence is unrelated — there is no logical connection to cohere',
            'The experiment should be described in more detail',
            '"Moreover" should be replaced with "Furthermore"'
          ],
          answer: 'The second sentence is unrelated — there is no logical connection to cohere',
          explanation: 'No linking word can fix a lack of logical connection between ideas. Cohesion requires both grammatical devices AND semantic relevance. The weather detail is irrelevant to the experimental results.'
        },
        {
          text: '"Firstly, education is important. Secondly, healthcare matters. Thirdly, infrastructure is needed. In conclusion, all three are important." — What is wrong with this cohesion?',
          options: [
            'The linking words are too simple for Band 7+',
            'Each point is undeveloped, and the "Firstly/Secondly/Thirdly" scaffolding substitutes for genuine idea-based cohesion',
            'There should be a "Fourthly"',
            '"In conclusion" is incorrect here'
          ],
          answer: 'Each point is undeveloped, and the "Firstly/Secondly/Thirdly" scaffolding substitutes for genuine idea-based cohesion',
          explanation: 'Mechanical numbering without developed ideas or meaningful transitions between points is a Band 5-6 pattern. Band 7+ essays connect paragraphs through ideas, not just sequential markers.'
        }
      ]
    }
  ],
  tips: [
    'Replace bare "this" with "this + noun" (this trend, this approach) to avoid ambiguity — examiners notice.',
    'Build a personal synonym bank for common IELTS topics (education, health, environment) to use lexical cohesion naturally.',
    'Read your essay backwards paragraph by paragraph — if each paragraph makes sense independently but not as a sequence, your transitions need work.',
    'The best cohesion is invisible: if the reader notices your linking devices, they are probably too heavy-handed.'
  ],
  dailyPractice: [
    'Take a paragraph from a news article and identify every cohesive device (reference, synonym, transition).',
    'Rewrite a 5-sentence paragraph replacing all repeated nouns with varied reference and synonyms.',
    'Practise writing two connected paragraphs using only echo and pivot transitions — no "Firstly/However" linkers.',
    'Read a Band 9 model essay and map the thematic progression pattern of each paragraph.'
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Advanced Cohesion Devices course for IELTS Writing Band 7+. Covers reference words (this, these, such, the former/latter), substitution and ellipsis, lexical cohesion through synonyms and collocations, thematic progression (known-to-new), and paragraph-level transitions beyond simple linkers. Aimed at candidates seeking sophisticated coherence and cohesion scores.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...advancedCohesionData,
      title: advancedCohesionData.title + ' (Foundations)',
      overview: { ...advancedCohesionData.overview, duration: '15-20 minutes' },
      sections: advancedCohesionData.sections.slice(0, Math.max(2, Math.ceil(advancedCohesionData.sections.length / 2))),
      exercises: advancedCohesionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...advancedCohesionData,
      title: advancedCohesionData.title + ' (Advanced)',
      overview: { ...advancedCohesionData.overview, duration: '30-35 minutes' },
      sections: advancedCohesionData.sections,
      exercises: advancedCohesionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(advancedCohesionData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return advancedCohesionData;
}
